// apps/roleforge-app/src/components/respond/RespondFinishControl.tsx
//
// Issue #1250 — the stakeholder-side "I'm finished" affordance.
//
// The agent's `request_thread_completion` tool parks the thread at
// `completion_proposed` and snapshots the interview (#1203), but deliberately
// leaves the thread open so the stakeholder can keep talking. Nothing ever
// reached `completed_self`: the only terminal transition was the OWNER's
// force-close. This is the missing control.
//
// Two-step on purpose. Ending your own interview is not undoable from this
// surface, and the button sits directly under a chat composer, so a single
// mis-tap must not close the interview.

import { useState } from 'react';

interface Props {
  /** Fires on confirm; should resolve once the server has recorded it. */
  onConfirm: () => Promise<void> | void;
  /** True once the thread is terminal — renders the acknowledgement instead. */
  finished: boolean;
  /** Suppresses the control while a turn is streaming. */
  disabled?: boolean;
  className?: string;
}

export function RespondFinishControl({ onConfirm, finished, disabled, className }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (finished) {
    return (
      <div
        role="status"
        data-testid="respond-finished-ack"
        className={`rounded-lg border border-line bg-paper px-4 py-3 text-sm text-ink ${className ?? ''}`}
      >
        <p className="font-medium">Thanks — your interview is marked complete.</p>
        <p className="mt-1 text-graphite">
          {"We've let the person who invited you know. You can close this tab."}
        </p>
      </div>
    );
  }

  if (!confirming) {
    return (
      <div className={className}>
        <button
          type="button"
          data-testid="respond-finish-button"
          disabled={disabled}
          onClick={() => setConfirming(true)}
          className="text-sm text-graphite underline underline-offset-4 hover:text-ink disabled:opacity-50"
        >
          {"I'm finished"}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="respond-finish-confirm"
      className={`rounded-lg border border-line bg-paper px-4 py-3 text-sm ${className ?? ''}`}
    >
      <p className="text-ink">
        Mark your interview as finished? This closes the conversation and tells
        the person who invited you that you are done.
      </p>
      {failed && (
        <p className="mt-2 text-graphite" data-testid="respond-finish-error">
          {"That didn't go through. Try again."}
        </p>
      )}
      <div className="mt-3 flex gap-3">
        <button
          type="button"
          data-testid="respond-finish-confirm-button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setFailed(false);
            try {
              await onConfirm();
            } catch {
              setFailed(true);
            } finally {
              setBusy(false);
            }
          }}
          className="rounded-md bg-ink px-3 py-1.5 text-sm text-paper disabled:opacity-50"
        >
          {busy ? 'Saving…' : "Yes, I'm finished"}
        </button>
        <button
          type="button"
          data-testid="respond-finish-cancel-button"
          disabled={busy}
          onClick={() => {
            setConfirming(false);
            setFailed(false);
          }}
          className="rounded-md px-3 py-1.5 text-sm text-graphite hover:text-ink disabled:opacity-50"
        >
          Keep talking
        </button>
      </div>
    </div>
  );
}
